import { useEffect, useState } from 'react';

import { load as loadBotd } from '@fingerprintjs/botd';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { captureCustomException } from 'App/Services/ErrorTracking/captureCustomException';

import { consoleInfoLog, isMobile } from '../Helpers';

/* --- helpers & checks --- */
const UA = () => navigator.userAgent;

const botUARegexes = [
    /bot/i,
    /crawl/i,
    /spider/i,
    /slurp/i,
    /mediapartners/i,
    /baiduspider/i,
    /yandex/i,
    /duckduckbot/i,
    /bingbot/i,
    /googlebot/i,
    /crawler/i,
    /curl/i,
    /wget/i,
    /headless/i,
];

const detectUserAgentBot = () => botUARegexes.some((re) => re.test(UA()));
const detectProcessPresence = () => typeof window.process !== 'undefined';

const detectNoWebRTC = () =>
    typeof RTCPeerConnection === 'undefined' &&
    typeof globalThis.webkitRTCPeerConnection === 'undefined';

const detectMimeTypesConsistency = () =>
    !navigator.mimeTypes || typeof navigator.mimeTypes.length !== 'number';

const detectLowColorDepth = () =>
    screen?.colorDepth > 0 && screen.colorDepth < 24;

const detectDeviceMemorySpoof = () => {
    const mem = navigator.deviceMemory;

    return typeof mem === 'number' && mem < 0.25;
};

const detectPerformance = () => {
    try {
        return !performance.getEntriesByType('navigation').length;
    } catch {
        return false;
    }
};

const detectHeadless = () => {
    const ua = UA().toLowerCase();

    return (
        navigator.webdriver === true ||
        /headlesschrome|headlessfirefox|phantomjs/.test(ua) ||
        (!window.outerWidth && !window.outerHeight) ||
        (window.screen &&
            (window.screen.width === 0 || window.screen.height === 0))
    );
};

const detectNavigatorProps = () => {
    const hasLangs =
        Array.isArray(navigator.languages) && navigator.languages.length > 0;
    const hasHC =
        typeof navigator.hardwareConcurrency === 'number' &&
        navigator.hardwareConcurrency > 0;

    return !(hasLangs && hasHC) || navigator.webdriver === true;
};

const detectWebGLRenderer = () => {
    try {
        const canvas = document.createElement('canvas');
        const gl =
            canvas.getContext('webgl') ||
            canvas.getContext('experimental-webgl');

        if (!gl) return false;

        const renderer = gl.getParameter(gl.RENDERER);

        return !!(
            renderer &&
            /swiftshader|llvmpipe|software rasterizer/i.test(renderer)
        );
    } catch {
        return false;
    }
};

const detectCanvasSpoofing = () => {
    try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        ctx?.fillText('x', 1, 1);
        const data = canvas.toDataURL();

        return !data || data.length < 100;
    } catch {
        return false;
    }
};

const detectNoTouchSupport = () => {
    if (!isMobile()) return false;

    return !(
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        ('msMaxTouchPoints' in navigator && navigator.msMaxTouchPoints > 0)
    );
};
const detectNoPlugins = () => {
    try {
        if (isMobile()) return false;
        return (
            'plugins' in navigator &&
            navigator.plugins &&
            navigator.plugins.length === 0
        );
    } catch {
        return false;
    }
};

const detectLangTimezoneMissing = () => {
    try {
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        return !navigator.language || !timeZone;
    } catch {
        return false;
    }
};

const detectProductSubSpoof = () => {
    try {
        const ua = navigator.userAgent || '';
        // Only flag on Blink/WebKit (Chrome/Safari family). Firefox uses 20100101 normally.
        const isBlinkOrWebKit =
            /(Chrome|Chromium|CriOS|Safari)/i.test(ua) && !/Firefox/i.test(ua);
        if (!isBlinkOrWebKit) return false;

        return !!(navigator.productSub && navigator.productSub !== '20030107');
    } catch {
        return false;
    }
};

const detectPushPermissionAnomaly = () => {
    try {
        const ua = navigator.userAgent || '';

        // Legit environments where Notification can be missing
        const isIOS = /iPhone|iPad|iPod/i.test(ua); // iOS Safari <16.4, many WKWebViews
        const isLiteBrowser = /Opera Mini|UCBrowser|Puffin/i.test(ua);
        const isInAppBrowser = /(FBAN|FBAV|Instagram|Snapchat|TikTok)/i.test(
            ua
        );
        const isOldAndroidWebView =
            /Android [0-4]\./i.test(ua) && /wv/.test(ua);

        const notif = globalThis.Notification;

        // Acceptable missing Notification on these platforms
        if (
            (isIOS || isLiteBrowser || isInAppBrowser || isOldAndroidWebView) &&
            typeof notif === 'undefined'
        ) {
            return false;
        }

        if (
            typeof notif === 'undefined' ||
            typeof notif.permission !== 'string'
        ) {
            return true; // suspicious elsewhere
        }

        return !['default', 'granted', 'denied'].includes(notif.permission);
    } catch {
        return true; // Fail closed for anomaly, but count as a WEAK signal in your scoring
    }
};

/* -------------------- Browser classification -------------------- */
const getBrowserFlags = () => {
    const ua = UA();

    return {
        isChrome:
            /chrome|crios|chromium/i.test(ua) &&
            !/\sOPR\/|\sEdg\//.test(ua) &&
            !/SamsungBrowser\//i.test(ua),
        isFirefox: /firefox/i.test(ua),
        isSafari:
            /safari/i.test(ua) &&
            !/chrome|chromium|crios|android|opr|edg|samsungbrowser/i.test(ua),
        isEdge: /\sEdg\//.test(ua),
        isOpera: /\sOPR\//.test(ua),
        isSamsung: /SamsungBrowser\//i.test(ua),
    };
};

function detectFontsSignals(components, ua = navigator.userAgent) {
    const isFirefox = /firefox/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isAndroidWV = /Android/i.test(ua) && /\bwv\b/.test(ua);
    const isChromiumDesktop =
        /Chrome|Chromium/i.test(ua) &&
        !/Mobile/i.test(ua) &&
        !/Edg|OPR|SamsungBrowser/i.test(ua);

    // Skip environments where fonts are commonly restricted or noisy
    if (isFirefox || isIOS || isAndroidWV || !isChromiumDesktop) {
        return { fired: false, label: '' };
    }

    const fonts = components?.fonts?.value;
    const fontsLen = Array.isArray(fonts) ? fonts.length : 0;

    const fontPrefs = components?.fontPreferences?.value;
    const hasFontPrefs =
        fontPrefs &&
        typeof fontPrefs === 'object' &&
        Object.keys(fontPrefs).length > 0;

    // STRONG: both missing where we expect them
    if (fontsLen === 0 && !hasFontPrefs) {
        return { fired: true, label: 'FontsMissing' };
    }

    // WEAK: only font preferences missing
    if (!hasFontPrefs) {
        return { fired: true, label: 'FontPrefsMissing' };
    }

    return { fired: false, label: '' };
}

/* -------------------- Scoring model -------------------- */
const WEAK = new Set([
    'NoPlugins',
    'AudioZero',
    'WebGLSoftware',
    'PerfAPIEmpty',
    'NoWebRTC',
    'DeviceMemorySpoof',
    'MimeTypesInconsistent',
    'ProcessDetected',
    'LowColorDepth',
    'PushPermissionAnomaly',
    'FontPrefsMissing',
]);

const thresholdFor = (b) => {
    if (b.isSafari || b.isFirefox) return 4; // privacy-heavy
    if (b.isEdge || b.isOpera || b.isSamsung) return 3;

    return 3; // Chrome baseline
};

const internalScore = (labels) =>
    labels.reduce((s, l) => {
        if (String(l).startsWith('Confidence')) return 0 + s; // skip confidence

        return s + (WEAK.has(l) ? 1 : 2);
    }, 0);

const labelForScore = (score, threshold) => {
    if (score >= threshold + 2) return 'Bot';
    if (score >= threshold) return 'BotSuspected';

    return 'Human';
};

export default function useDetectBot() {
    const [isBot, setIsBot] = useState(null);
    const [visitorId, setVisitorId] = useState(null);
    const [failedChecks, setFailedChecks] = useState([]);

    useEffect(() => {
        let cancelled = false;

        (async function run() {
            // --- PUPPETEER PREBUILD SHORT-CIRCUIT ---
            if (window.__PUPPETEER_PREBUILD_MODE__) {
                if (!cancelled) {
                    setIsBot(false);
                    setFailedChecks([]);
                }
                return;
            }

            try {
                // 1) BotD primary
                const botd = await loadBotd();
                const botdResult = await botd.detect();

                // 2) FPJS auxiliary
                const fp = await FingerprintJS.load();
                const {
                    visitorId: vid,
                    components,
                    confidence,
                } = await fp.get();

                if (cancelled) return;

                setVisitorId(vid);

                const browser = getBrowserFlags();
                const labels = [];

                // Confidence
                labels.push(`Confidence${confidence?.score ?? 'N/A'}`);

                const fontSig = detectFontsSignals(components);

                if (fontSig.fired) labels.push(fontSig.label);

                if (
                    components?.audio &&
                    typeof components.audio.value === 'number' &&
                    components.audio.value === 0
                )
                    labels.push('AudioZero');

                const checks = [
                    ['Headless', detectHeadless],
                    ['BotSignature', detectUserAgentBot],
                    ['PerfAPIEmpty', detectPerformance],
                    ['NavProps', detectNavigatorProps],
                    ['WebGLSoftware', detectWebGLRenderer],
                    ['CanvasFingerprint', detectCanvasSpoofing],
                    ['LangTimeMismatch', detectLangTimezoneMissing],
                    ['NoWebRTC', detectNoWebRTC],
                    ['ProcessDetected', detectProcessPresence],
                    ['PushPermissionAnomaly', detectPushPermissionAnomaly],
                    ['MimeTypesInconsistent', detectMimeTypesConsistency],
                    ['LowColorDepth', detectLowColorDepth],
                    ['NoPlugins', detectNoPlugins],
                    ['NoTouchSupport', detectNoTouchSupport],
                    ['ProductSubSpoof', detectProductSubSpoof],
                    ['DeviceMemorySpoof', detectDeviceMemorySpoof],
                ];

                for (const [label, fn] of checks) {
                    try {
                        if (fn()) labels.push(label);
                    } catch (error) {
                        captureCustomException(error, 'BotCheckError', {
                            check_failed: label,
                        });
                    }
                }

                const score = internalScore(labels);
                const threshold = thresholdFor(browser);
                const label = labelForScore(score, threshold);

                const botdFlag = !!botdResult?.bot;
                const verdict = botdFlag || label !== 'Human';
                const finalFailedChecks = [
                    ...labels,
                    ...(label === 'BotSuspected' ? ['BotSuspected'] : []),
                ];

                if (!cancelled) {
                    consoleInfoLog(
                        `## Bot Detector Data: visitorId - ${vid}, isBot - ${verdict}, failedChecks - ${finalFailedChecks}`,
                        'init'
                    );
                    setIsBot(verdict);
                    setFailedChecks(finalFailedChecks);
                }
            } catch (e) {
                if (!cancelled) {
                    setIsBot(false);
                    setFailedChecks(['DetectionError']);
                    captureCustomException(e, 'BotDetectionError');
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    return { visitorId, isBot, failedChecks };
}
